import React from "react";
import { Navigate, Route, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LoadingScreen from "@/components/shared/LoadingScreen";
import useAuthSession from "@/middleware/hooks/useAuthSession";
import { featureFlags } from "@/config/featureFlags";
import MeetNewPage from "./MeetNewPage";
import MeetJoinPage from "./MeetJoinPage";
import VanityManagePage from "./VanityManagePage";

/**
 * Auth guard for the standalone meet pages. Same semantics as App.tsx's
 * `Protected`, PLUS it carries the visited path to `/login` in router state
 * (`{ from }`) so a shared meeting link survives the login bounce — a join
 * link is exactly the kind of deep link people open logged-out. `LoginForm`
 * and `UnProtected` honor it via `readLoginReturnPath` (strictly validated —
 * internal absolute paths only).
 */
export const MeetProtected = ({
  children,
}: {
  children: React.JSX.Element;
}) => {
  const { t } = useTranslation();
  const { isLoggedIn, loading } = useAuthSession();
  const location = useLocation();
  if (loading) {
    return <LoadingScreen title={t("loading")} />;
  }
  return isLoggedIn ? (
    children
  ) : (
    <Navigate
      to="/login"
      replace
      // Full internal location (path + query + fragment) so nothing on a deep
      // link is lost across the login bounce. Validated on the READ side
      // (readLoginReturnPath) before it is ever used as a navigation target.
      state={{ from: location.pathname + location.search + location.hash }}
    />
  );
};

/**
 * Standalone-meeting routes (Product B V0) — returned as elements for App.tsx
 * to splice DIRECTLY into its `<Routes>` (react-router flattens fragments, but
 * only `<Route>`/fragments are legal `<Routes>` children, hence a function
 * returning elements rather than a component).
 *
 * FLAG GATING: `null` unless BOTH `orionMeetUi` (the Orion Meet master
 * kill-switch) AND `standaloneMeet` (`VITE_STANDALONE_MEET_ENABLED`) are true —
 * the master flag dominates, matching the rest of the meet layer. When off the
 * routes are NOT registered at all: `/meet/new` / `/meet/:meetingId` fall
 * through to no match (both are two-segment paths, so the `/:chatId?`
 * catch-all can't claim them) — no dead surface in prod/beta bundles, where
 * both env vars are false.
 *
 * Registered ALONGSIDE the static `/meet/guest` route: react-router ranks
 * static segments above dynamic ones, so `/meet/guest` and `/meet/new` always
 * win over `/meet/:meetingId` regardless of registration order.
 */
export function standaloneMeetRoutes(): React.JSX.Element | null {
  if (!featureFlags.orionMeetUi || !featureFlags.standaloneMeet) {
    return null;
  }
  return (
    <>
      <Route
        path="/meet/new"
        element={
          <MeetProtected>
            <MeetNewPage />
          </MeetProtected>
        }
      />
      {/*
       * Vanity-alias CLAIM + MANAGE page. Static two-segment path → no root
       * collision, and outranks `/meet/:meetingId` (static > dynamic). Gated by
       * the additional `meetVanity` flag ON TOP of the master + standalone flags
       * already enforced above, so it registers only when the whole vanity
       * surface is enabled. When off it is not registered at all.
       */}
      {featureFlags.meetVanity && (
        <Route
          path="/meet/vanity"
          element={
            <MeetProtected>
              <VanityManagePage />
            </MeetProtected>
          }
        />
      )}
      <Route
        path="/meet/:meetingId"
        element={
          <MeetProtected>
            <MeetJoinPage />
          </MeetProtected>
        }
      />
    </>
  );
}
